import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Paper,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import TextInput from "../../Components/InputComponents/TextInput";
import SelectInput from "../../Components/InputComponents/SelectInput";
import CommonButton from "../../Components/InputComponents/CommonButton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../Components/ToastComponent/ToastNotification";
import TextAreaInput from "../../Components/InputComponents/TextAreaInput";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { decryptId } from "../../Components/CryptoComponent";

const EditClient = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // this is the encrypted id
  const decryptedId = decryptId(decodeURIComponent(id));
  const location = useLocation();

  const paginationState = location?.state?.pagination || {
    page: 0,
    rowsPerPage: 25,
  };

  const filterData = location?.state?.filterData || {
    name: "",
    phone: "",
    email: "",
    country: "",
    companyType: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    status: "",
  };
  const open = location?.state?.open || "";

  const [clientData, setClientData] = useState({
    CD_Name: "",
    CD_Email: "",
    CD_Phone: "",
    CD_Address: "",
    CD_Country: "",
    CD_CompanyType: "",
    CD_ContactPerson: "",
    CD_ContactPerson_Email: "",
    CD_ContactPerson_Phone: "",
    CD_Notes: "",
    CD_Status: "",
    CD_Branches: "",
  });

  const [errors, setErrors] = useState({});

  const inputRefs = {
    CD_Name: useRef(),
    CD_Country: useRef(),
    CD_CompanyType: useRef(),
    CD_ContactPerson_Email: useRef(),
    CD_ContactPerson_Phone: useRef(),
  };

  const [isDisabled, setIsDisabled] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(false);

  const [loading, setLoading] = useState({});
  const [countryList, setCountryList] = useState([]);
  const [companyTypeList, setCompanyTypeList] = useState([]);

  const mandatoryFields = [
    { field: "CD_Name", label: "Full Name" },
    { field: "CD_Country", label: "Country" },
    { field: "CD_CompanyType", label: "Company Type" },
    { field: "CD_ContactPerson_Email", label: "Contact Email", type: "email" },
    { field: "CD_ContactPerson_Phone", label: "Contact Phone Number" },
    { field: "CD_Status", label: "Status" },
  ];

  const validateForm = () => {
    let formErrors = {};
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const phoneRegex = /^[0-9]{10}$/; // Assuming 10-digit phone numbers for validation
    

    for (let { field, label, type } of mandatoryFields) {
      const value = clientData[field];

      if (!value) {
        formErrors[field] = `${label} is required.`;
      } else if (type === "email" && !emailRegex.test(value)) {
        formErrors[field] = `Please enter a valid ${label}.`;
      } else if (type === "phone" && !phoneRegex.test(value)) {
        formErrors[field] = `Please enter a valid ${label}.`;
      }
    }

    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      // Display the errors in a toast if validation fails
      Object.values(formErrors).forEach((error) => {
        errorNotify(error);
      });
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors({
      ...errors,
      [name]: "",
    });

    setClientData({
      ...clientData,
      [name]: value == "" ? null : value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsDisabled(true);
      setLoaderOpen(true);

      axiosAPI
        ?.post(`/Client/Edit/${decryptedId}`, clientData)
        .then((res) => {
          setIsDisabled(false);
          setLoaderOpen(false);

          if (res?.data?.success) {
            successNotify(res?.data?.message);
            navigate(`/Clients`, {
              state: {
                pagination: paginationState,
                filterData: filterData,
                open: open,
              },
            });
          } else {
            infoNotify(res?.data?.message);
          }
        })
        .catch((err) => {
          errorNotify("Something went wrong!");
          setIsDisabled(false);
          setLoaderOpen(false);
        });
    }
  };

  const getCountries = async () => {
    try {
      setLoading((prev) => ({ ...prev, countries: true }));
      const res = await axiosAPI.get(`/RecruitmentCountry/listCountry`);
      setCountryList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, countries: false }));
    }
  };

  const getCompanyType = async () => {
    try {
      setLoading((prev) => ({ ...prev, companyType: true }));
      const res = await axiosAPI.get(`/CompanyType/active/list`);
      setCompanyTypeList(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      try {
        const resFallback = await axiosAPI.get(`/CompanyType/list`);
        setCompanyTypeList(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    } finally {
      setLoading((prev) => ({ ...prev, companyType: false }));
    }
  };

  const getData = async () => {
    try {
      const res = await axiosAPI.get(`/Client/listbyId/${decryptedId}`);
      if (res?.data?.data) {
        setClientData(res?.data?.data);
        setLoaderOpen(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const firstErrorField = mandatoryFields.find(
      (field) => errors[field.field]
    );

    if (firstErrorField && inputRefs[firstErrorField.field]?.current) {
      inputRefs[firstErrorField.field].current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      inputRefs[firstErrorField.field].current.focus(); // optional
    }
  }, [errors]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getCountries();
    getCompanyType();
    getData();
  }, []);

  return (
    <Paper
      sx={{
        padding: { xs: 2, sm: 3, md: 5 },
        margin: "auto",
        position: "relative",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        Edit Client
      </Typography>

      {/* Basic Information */}
      <Grid
        container
        sx={{
          border: "1px solid lightgrey",
          borderRadius: "10px",
          padding: "20px",
        }}
        my={2}
      >
        {/* Basic Information */}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,

              fontFamily: "Geist, sans-serif",
              mt: 2,
            }}
          >
            Basic Information
          </Typography>
        </Grid>

        {/* Client Name */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Name <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            label="Full Name"
            placeholder="Enter Client Name"
            name="CD_Name"
            value={clientData.CD_Name}
            onChange={handleChange}
            inputRef={inputRefs.CD_Name}
            error={!!errors.CD_Name}
          />
        </Grid>

        {/* Email */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Email
          </Typography>
          <TextInput
            label="Email Address"
            placeholder="Enter Email Address"
            type="email"
            name="CD_Email"
            value={clientData.CD_Email}
            onChange={handleChange}
          />
        </Grid>

        {/* Phone Number */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Contact Number
          </Typography>
          <TextInput
            label="Phone Number"
            placeholder="Enter Contact Number"
            type="tel"
            name="CD_Phone"
            value={clientData.CD_Phone}
            onChange={handleChange}
            allowOnlyNumbers={true}
            maxLength={15}
          />
        </Grid>

        {/* Address */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Address
          </Typography>
          <TextInput
            label="Address"
            placeholder="Enter Address"
            name="CD_Address"
            value={clientData.CD_Address}
            onChange={handleChange}
          />
        </Grid>

        {/* Country Selection */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Country <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            name="CD_Country"
            value={clientData.CD_Country}
            options={countryList?.map((item) => ({
              value: item?.CL_ID,
              label: item?.CL_Name,
            }))}
            placeholder="Select Country"
            onChange={handleChange}
            inputRef={inputRefs.CD_Country}
            error={!!errors.CD_Country}
            loading={loading?.countries}
          />
        </Grid>

        {/* Client Type */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Company Type <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            name="CD_CompanyType"
            value={clientData.CD_CompanyType}
            options={companyTypeList?.map((item) => ({
              value: item.CT_Id,
              label: item.CT_Name,
            }))}
            placeholder="Select Company Type"
            onChange={handleChange}
            inputRef={inputRefs.CD_CompanyType}
            error={!!errors.CD_CompanyType}
            loading={loading?.companyType}
          />
        </Grid>
      </Grid>

      {/* Contact Person Details */}
      <Grid
        container
        sx={{
          border: "1px solid lightgrey",
          borderRadius: "10px",
          padding: "20px",
        }}
        my={2}
      >
        {/* Contact Person Details */}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,

              fontFamily: "Geist, sans-serif",
              mt: 2,
            }}
          >
            Contact Person Details
          </Typography>
        </Grid>

        {/* Contact Person Name */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Name
          </Typography>
          <TextInput
            label="Contact Person Name"
            placeholder="Enter Contact Person Name"
            name="CD_ContactPerson"
            value={clientData.CD_ContactPerson}
            onChange={handleChange}
          />
        </Grid>

        {/* Contact Person Email */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Email <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            label="Contact Person Email"
            placeholder="Enter Contact Person's Email Address"
            type="email"
            name="CD_ContactPerson_Email"
            value={clientData.CD_ContactPerson_Email}
            onChange={handleChange}
            inputRef={inputRefs.CD_ContactPerson_Email}
            error={!!errors.CD_ContactPerson_Email}
          />
        </Grid>

        {/* Contact Person Phone Number */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Contact Number
            <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            label="Phone Number"
            placeholder="Enter Phone Number"
            type="tel"
            name="CD_ContactPerson_Phone"
            value={clientData.CD_ContactPerson_Phone}
            onChange={handleChange}
            allowOnlyNumbers={true}
            maxLength={15}
            inputRef={inputRefs.CD_ContactPerson_Phone}
            error={!!errors.CD_ContactPerson_Phone}
          />
        </Grid>
      </Grid>

      {/* Additional Information */}

      <Grid
        container
        sx={{
          border: "1px solid lightgrey",
          borderRadius: "10px",
          padding: "20px",
        }}
        my={2}
      >
        {/* Additional Information */}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              fontFamily: "Geist, sans-serif",
              mt: 2,
            }}
          >
            Additional Information
          </Typography>
        </Grid>

        {/* Branches */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Branches
          </Typography>
          <TextInput
            label="Branches"
            placeholder="Branches"
            name="CD_Branches"
            value={clientData.CD_Branches}
            onChange={handleChange}
          />
        </Grid>

         {/* Client Username*/}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Username <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            label="User Name"
            placeholder="Enter User Name"
            name="CI_UserName"
            value={clientData.CD_UserName}
            isDisabled={true}
            error={!!errors.CD_UserName}
            inputRef={inputRefs.CD_UserName}
          />
        </Grid>

         {/* Client Status */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Status <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            name="CD_Status"
            value={clientData.CD_Status}
            options={[
              { value: 1, label: "Active" },
              { value: 2, label: "Inactive" },
            ]}
            placeholder="Select Status"
            onChange={handleChange}
            error={!!errors.CD_Status}
          />
        </Grid>

        {/* Notes */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={12} md={4} lg={4} xl={3}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Notes
          </Typography>
          <TextAreaInput
            label="Notes"
            placeholder="Notes"
            name="CD_Notes"
            value={clientData.CD_Notes}
            onChange={handleChange}
          />
        </Grid>

      </Grid>

      {/* Buttons */}
      <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        <Grid item sx={{ padding: "5px" }}>
          <CommonButton
            variant="outlined"
            customColor="#B0BEC5"
            onClick={() => {
              navigate(`/Clients`, {
                state: {
                  pagination: paginationState,
                  filterData: filterData,
                  open: open,
                },
              });
            }}
          >
            Cancel
          </CommonButton>
        </Grid>
        <Grid item sx={{ padding: "5px" }}>
          <CommonButton
            variant="contained"
            customColor="#007BFF"
            onClick={handleSubmit}
            disabled={isDisabled}
          >
           {isDisabled?'Saving...':'Save'}
          </CommonButton>
        </Grid>
      </Grid>
      <Backdrop
        open={loaderOpen}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: "#fff",
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          flexDirection: "column",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
};

export default EditClient;
